/**
 * UserDetailPage - Detailansicht/Neuanlage eines Benutzers
 * 
 * Pattern: Analog zu CustomerDetailPage (keine Modals)
 * 
 * Sektionen:
 * - Grunddaten (Name, Vorname, Nachname, E-Mail)
 * - Rolle & Status
 * - Passwort (nur bei Create Pflicht für Nicht-WORKER)
 * - RFID/Chip-Zuordnung
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Users, ArrowLeft, Save, AlertCircle, CreditCard, Info, Mail, KeyRound, Loader2, CheckCircle, ShieldAlert } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { useAuth } from '../../App';
import { sendInvite, resetUser2FA, ApiException } from '../../api/client';

type UserRole = 'WORKER' | 'ADMIN' | 'SUPERADMIN' | 'SYSTEM';

interface User {
  id: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  role: UserRole;
  isActive: boolean;
  twoFactorEnabled: boolean;
  nfcChipId: string | null;
  rfidPrintedCode: string | null;
}

const ASSIGNABLE_ROLES: { value: UserRole; label: string }[] = [
  { value: 'WORKER', label: 'Mitarbeiter' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'SUPERADMIN', label: 'Superadmin' },
];

interface FormData {
  displayName: string;
  firstName: string;
  lastName: string;
  email: string;
  role: UserRole;
  password: string;
  nfcChipId: string;
  rfidPrintedCode: string;
}

const INITIAL_FORM: FormData = {
  displayName: '',
  firstName: '',
  lastName: '',
  email: '',
  role: 'WORKER',
  password: '',
  nfcChipId: '',
  rfidPrintedCode: '',
};

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isNew = id === 'new';
  
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [displayNameManuallyEdited, setDisplayNameManuallyEdited] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [resetting2FA, setResetting2FA] = useState(false);
  const [reset2FASuccess, setReset2FASuccess] = useState('');
  
  const [formData, setFormData] = useState<FormData>(INITIAL_FORM);

  // Rollen-Hierarchie: Welche Rollen kann der aktuelle User vergeben?
  const getAllowedRoles = (): UserRole[] => {
    if (!currentUser) return [];
    if (currentUser.role === 'SYSTEM' || currentUser.role === 'SUPERADMIN') {
      return ['WORKER', 'ADMIN', 'SUPERADMIN'];
    }
    if (currentUser.role === 'ADMIN') {
      return ['WORKER', 'ADMIN'];
    }
    return [];
  };

  const allowedRoles = getAllowedRoles();
  const isWorkerRole = formData.role === 'WORKER';
  const requiresFullProfile = !isWorkerRole;

  // Load user details
  const loadUser = async () => {
    if (isNew) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/users/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUser(data);
        setFormData({
          displayName: data.displayName,
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          email: data.email || '',
          role: data.role,
          password: '',
          nfcChipId: data.nfcChipId || '',
          rfidPrintedCode: data.rfidPrintedCode || '',
        });
        setDisplayNameManuallyEdited(true);
      } else if (res.status === 404) {
        navigate('/admin/users');
      }
    } catch (err) {
      console.error('Fehler beim Laden des Benutzers:', err);
      setError('Benutzer konnte nicht geladen werden');
    }
    setLoading(false);
  };

  useEffect(() => {
    loadUser();
  }, [id]);

  // Track changes
  const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setError(null);
  };

  // Auto-Suggest für displayName
  const handleFirstNameChange = (value: string) => {
    updateField('firstName', value);
    if (!displayNameManuallyEdited && isNew) {
      const suggested = `${value} ${formData.lastName}`.trim();
      if (suggested) {
        setFormData(prev => ({ ...prev, firstName: value, displayName: suggested }));
      }
    }
  };

  const handleLastNameChange = (value: string) => {
    updateField('lastName', value);
    if (!displayNameManuallyEdited && isNew) {
      const suggested = `${formData.firstName} ${value}`.trim();
      if (suggested) {
        setFormData(prev => ({ ...prev, lastName: value, displayName: suggested }));
      }
    }
  };

  const handleDisplayNameChange = (value: string) => {
    updateField('displayName', value);
    setDisplayNameManuallyEdited(true);
  };

  // Save handler
  const handleSave = async () => {
    // Validation
    if (!formData.displayName.trim()) {
      setError('Anzeigename ist erforderlich');
      return;
    }

    if (isNew) {
      if (isWorkerRole) {
        if (!formData.firstName.trim() && !formData.lastName.trim()) {
          setError('Für Mitarbeiter muss mindestens Vorname oder Nachname angegeben werden');
          return;
        }
      } else {
        const missing: string[] = [];
        if (!formData.email.trim()) missing.push('E-Mail');
        if (!formData.password.trim()) missing.push('Passwort');
        if (!formData.firstName.trim()) missing.push('Vorname');
        if (!formData.lastName.trim()) missing.push('Nachname');
        if (missing.length > 0) {
          setError(`Pflichtfelder für ${formData.role}: ${missing.join(', ')}`);
          return;
        }
      }
    }

    setSaving(true);
    setError(null);

    try {
      const payload: Record<string, unknown> = {
        displayName: formData.displayName.trim(),
        firstName: formData.firstName.trim() || null,
        lastName: formData.lastName.trim() || null,
        email: formData.email.trim() || null,
        role: formData.role,
        nfcChipId: formData.nfcChipId.trim() || null,
        rfidPrintedCode: formData.rfidPrintedCode.trim() || null,
      };

      if (formData.password) {
        payload.password = formData.password;
      }

      const url = isNew ? '/api/admin/users' : `/api/admin/users/${id}`;
      const method = isNew ? 'POST' : 'PATCH';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const saved = await res.json();
        if (isNew) {
          navigate(`/admin/users/${saved.id}`, { replace: true });
        } else {
          setUser(saved);
          setHasChanges(false);
          // Reload to get fresh data
          loadUser();
        }
      } else {
        const err = await res.json().catch(() => ({ error: 'Speichern fehlgeschlagen' }));
        setError(err.error || 'Speichern fehlgeschlagen');
      }
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError('Netzwerkfehler beim Speichern');
    }

    setSaving(false);
  };

  // Toggle active status
  const handleToggleActive = async () => {
    if (!user || isNew) return;
    
    try {
      const res = await fetch(`/api/admin/users/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ isActive: !user.isActive }),
      });
      
      if (res.ok) {
        loadUser();
      }
    } catch (err) {
      console.error('Fehler beim Statuswechsel:', err);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/admin/users"
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Users className="w-6 h-6" />
              {isNew ? 'Neuer Benutzer' : user?.displayName || 'Benutzer'}
            </h1>
            {!isNew && user && (
              <p className="text-sm text-slate-500 mt-0.5">
                {user.email || 'Keine E-Mail'}
                {user.twoFactorEnabled && ' · 2FA aktiv'}
              </p>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saving || (!hasChanges && !isNew)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Sektion: Grunddaten */}
        <section className="bg-white rounded-lg border p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
          
          {isNew && isWorkerRole && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              Für Mitarbeiter muss mindestens Vorname oder Nachname angegeben werden.
            </div>
          )}
          
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Vorname {requiresFullProfile && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleFirstNameChange(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={requiresFullProfile ? 'Pflicht' : 'Optional'}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nachname {requiresFullProfile && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleLastNameChange(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={requiresFullProfile ? 'Pflicht' : 'Optional'}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Anzeigename <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => handleDisplayNameChange(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Wird automatisch aus Vor-/Nachname vorgeschlagen"
            />
          </div>
          
          {/* E-Mail: Nur anzeigen wenn Rolle != WORKER */}
          {!isWorkerRole && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                E-Mail <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => updateField('email', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Pflicht für Login"
              />
            </div>
          )}
        </section>

        {/* Sektion: Rolle & Status */}
        <section className="bg-white rounded-lg border p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 border-b pb-2">Rolle & Status</h2>
          
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Rolle <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.role}
                onChange={(e) => updateField('role', e.target.value as UserRole)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {ASSIGNABLE_ROLES.filter(r => allowedRoles.includes(r.value)).map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {!isNew && user && !allowedRoles.includes(user.role) && (
                <p className="text-xs text-amber-600 mt-1">
                  Aktuelle Rolle kann nicht geändert werden (höhere Berechtigung erforderlich)
                </p>
              )}
            </div>
            
            {!isNew && user && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Status
                </label>
                <button
                  onClick={handleToggleActive}
                  className={`w-full px-3 py-2 rounded-lg border text-left flex items-center justify-between ${
                    user.isActive 
                      ? 'bg-green-50 border-green-200 text-green-800' 
                      : 'bg-red-50 border-red-200 text-red-800'
                  }`}
                >
                  <span>{user.isActive ? 'Aktiv' : 'Inaktiv'}</span>
                  <span className="text-xs opacity-70">Klick zum Wechseln</span>
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Sektion: Passwort - Nur anzeigen wenn Rolle != WORKER */}
        {!isWorkerRole && (
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 border-b pb-2">Passwort</h2>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Passwort {isNew && <span className="text-red-500">*</span>}
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => updateField('password', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={isNew ? 'Pflicht für Login' : 'Leer lassen um nicht zu ändern'}
              />
              {!isNew && (
                <p className="text-xs text-slate-500 mt-1">
                  Leer lassen, um das aktuelle Passwort beizubehalten
                </p>
              )}
            </div>
          </section>
        )}

        {/* Sektion: RFID/Chip */}
        <section className="bg-white rounded-lg border p-6 space-y-4">
          <div className="flex items-center justify-between border-b pb-2">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              RFID/Chip
            </h2>
          </div>
          
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Chip-ID (NFC)
              </label>
              <input
                type="text"
                value={formData.nfcChipId}
                onChange={(e) => updateField('nfcChipId', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="z.B. 04:A3:12:..."
              />
              <p className="text-xs text-slate-500 mt-1">
                Ausgelesene UID des RFID-Chips
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Aufgedruckte Nummer
              </label>
              <input
                type="text"
                value={formData.rfidPrintedCode}
                onChange={(e) => updateField('rfidPrintedCode', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="z.B. 12345678"
              />
              <p className="text-xs text-slate-500 mt-1">
                Auf dem Chip aufgedruckte Nummer
              </p>
            </div>
          </div>
        </section>

        {/* Sektion: Aktionen (nur bei bestehenden Usern mit Nicht-WORKER Rolle) */}
        {!isNew && user && user.role !== 'WORKER' && (
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <h2 className="text-lg font-semibold text-slate-900 border-b pb-2">Aktionen</h2>
            
            {/* Einladung senden */}
            {user.email && (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    Einladung senden
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Sendet eine E-Mail mit Link zum Passwort setzen
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {inviteSuccess && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      {inviteSuccess}
                    </span>
                  )}
                  <button
                    onClick={async () => {
                      setInviting(true);
                      setInviteSuccess('');
                      setError(null);
                      try {
                        await sendInvite(user.id);
                        setInviteSuccess('Gesendet');
                      } catch (err) {
                        if (err instanceof ApiException) {
                          setError(err.data.error);
                        } else {
                          setError('Fehler beim Senden der Einladung');
                        }
                      } finally {
                        setInviting(false);
                      }
                    }}
                    disabled={inviting}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    {inviting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    Einladung senden
                  </button>
                </div>
              </div>
            )}

            {/* 2FA zuruecksetzen */}
            {user.twoFactorEnabled && (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <KeyRound className="w-4 h-4 text-slate-400" />
                    2FA zuruecksetzen
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    User muss 2FA beim naechsten Login neu einrichten
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {reset2FASuccess && (
                    <span className="text-sm text-green-600 flex items-center gap-1">
                      <CheckCircle className="w-4 h-4" />
                      {reset2FASuccess}
                    </span>
                  )}
                  <button
                    onClick={async () => {
                      if (!confirm('2FA fuer diesen User wirklich zuruecksetzen?')) return;
                      setResetting2FA(true);
                      setReset2FASuccess('');
                      setError(null);
                      try {
                        await resetUser2FA(user.id);
                        setReset2FASuccess('Zurueckgesetzt');
                        loadUser();
                      } catch (err) {
                        if (err instanceof ApiException) {
                          setError(err.data.error);
                        } else {
                          setError('Fehler beim Zuruecksetzen');
                        }
                      } finally {
                        setResetting2FA(false);
                      }
                    }}
                    disabled={resetting2FA}
                    className="px-3 py-1.5 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700 disabled:opacity-50 flex items-center gap-1"
                  >
                    {resetting2FA ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
                    2FA zuruecksetzen
                  </button>
                </div>
              </div>
            )}

            {/* Warnung wenn kein Passwort und keine Einladung */}
            {!user.email && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Dieser User hat keine E-Mail-Adresse. Bitte E-Mail setzen und dann Einladung senden, damit sich der User einloggen kann.</span>
              </div>
            )}
          </section>
        )}

        {/* Hinweis */}
        {!isNew && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Hinweis zu historischen Daten</p>
              <p className="mt-1">
                Änderungen am Benutzerstamm wirken sich nicht auf bereits erfasste Vorgänge aus.
                Diese enthalten Snapshot-Daten zum Zeitpunkt der Erfassung.
              </p>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
